// ─────────────────────────────────────────────────────────────────────────────
// useDetectorStates
//
// Loads every detector_state row visible to the current user (one query),
// and provides ergonomic helpers for the UI:
//
//   - resolve({ accountId, detectorId }) → effective state row (or stub)
//   - setStatus({ accountId, detectorId, status, ... }) → optimistic UPSERT
//
// Used by BehaviorAnalytics and RiskDiscipline. Both run the same flow
// (read all rows once, render each card with resolved state, mutate on
// click). Keeping the logic in one hook means the components don't
// duplicate state-management boilerplate.
//
// Optimistic update semantics:
//   When setStatus is called, we update local state IMMEDIATELY (so the
//   UI flips at click time, not after a network round trip). The DB
//   write happens in the background. If it fails, we roll back the
//   local state and log the error — the UI returns to its prior
//   appearance and the user sees an error in the console.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";
import {
  loadDetectorStates,
  resolveDetectorState,
  setDetectorStatus,
} from "../lib/detectorState";

export function useDetectorStates(userId) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Stash the latest rows in a ref so callbacks can read fresh state without
  // having to re-create themselves whenever `rows` updates.
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  // Initial load
  useEffect(() => {
    if (!userId) {
      setRows([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await loadDetectorStates({ userId });
        if (cancelled) return;
        setRows(Array.isArray(data) ? data : []);
      } catch (err) {
        if (cancelled) return;
        console.error("[useDetectorStates] load failed:", err);
        setError(err);
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Resolve effective state for a given (account, detector). Pure function
  // over the current rows snapshot.
  const resolve = useCallback(
    ({ accountId, detectorId }) =>
      resolveDetectorState(rowsRef.current, { accountId, detectorId }),
    []
  );

  // Optimistic UPSERT. The caller passes everything that setDetectorStatus
  // accepts; we patch local state immediately, then write through.
  //
  // Defensive: refuses to write user-wide rows (accountId === null/undefined)
  // unless the caller passes `allowUserWide: true`. This stops silent global
  // writes when the UI's selected-account state happens to be falsy. The
  // future "Apply to all accounts" toggle should pass allowUserWide=true.
  const setStatus = useCallback(async (args) => {
    if (!userId) return null;
    if (!args || !args.detectorId || !args.status) {
      console.warn("[useDetectorStates] setStatus called with bad args:", args);
      return null;
    }
    if ((args.accountId === null || args.accountId === undefined) && !args.allowUserWide) {
      console.warn(
        "[useDetectorStates] refusing user-wide write without allowUserWide=true. " +
        "Select an account first. args:",
        args
      );
      return null;
    }

    const accountId = args.accountId ?? null;
    const detectorId = args.detectorId;

    // Build the optimistic patched row from the args + existing row (if any)
    const existing = rowsRef.current.find(
      r => r.account_id === accountId && r.detector_id === detectorId
    );
    const now = new Date().toISOString();
    const optimistic = {
      ...(existing || {}),
      user_id:        userId,
      account_id:     accountId,
      detector_id:    detectorId,
      status:         args.status,
      target_metric:   args.targetMetric   !== undefined ? args.targetMetric   : existing?.target_metric,
      target_value:    args.targetValue    !== undefined ? args.targetValue    : existing?.target_value,
      target_baseline: args.targetBaseline !== undefined ? args.targetBaseline : existing?.target_baseline,
      target_date:     args.targetDate     !== undefined ? args.targetDate     : existing?.target_date,
      notes:           args.notes          !== undefined ? args.notes          : existing?.notes,
      acknowledged_at: args.status === "acknowledged"  ? now : existing?.acknowledged_at,
      working_on_at:   args.status === "working_on_it" ? now : existing?.working_on_at,
      dismissed_at:    args.status === "dismissed"     ? now : existing?.dismissed_at,
      updated_at:      now,
    };

    // Patch local first
    setRows(prev => {
      const idx = prev.findIndex(
        r => r.account_id === accountId && r.detector_id === detectorId
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = optimistic;
        return next;
      }
      return [...prev, optimistic];
    });

    // Write through
    try {
      const saved = await setDetectorStatus({ userId, ...args });
      // Replace the optimistic row with the canonical version from the DB
      // (timestamps, generated ID, etc).
      if (saved && saved.detector_id) {
        setRows(prev => prev.map(r =>
          (r.account_id === accountId && r.detector_id === detectorId) ? saved : r
        ));
      }
      return saved;
    } catch (err) {
      console.error("[useDetectorStates] setStatus failed, rolling back:", err);
      // Roll back: if there was no prior row, remove the optimistic one.
      // If there was one, restore it.
      setRows(prev => {
        const idx = prev.findIndex(
          r => r.account_id === accountId && r.detector_id === detectorId
        );
        if (idx < 0) return prev;
        if (!existing) {
          const next = [...prev];
          next.splice(idx, 1);
          return next;
        }
        const next = [...prev];
        next[idx] = existing;
        return next;
      });
      setError(err);
      return null;
    }
  }, [userId]);

  return { rows, loading, error, resolve, setStatus };
}
